/**
 * Gắn `X-Anon-Token` vào MỌI lời gọi backend từ trình duyệt.
 *
 * Patch `window.fetch` thay vì sửa từng hàm trong `boide-api.ts`: file đó có hàng
 * trăm lời gọi, và nhiều lời gọi (login, gg-login, sign-up, forgot-password…)
 * không đi qua `authHeaders()`. Sót một chỗ là chỗ đó 401 khi backend bật
 * rate-limit — đúng những màn hình khách chưa đăng nhập cần nhất.
 *
 * Phạm vi: URL thuộc `BOIDE_API_URL` hoặc `BOIDE_ACCOUNT_API_URL` (account-be,
 * dùng chung Redis nên chấp nhận cùng token ẩn danh), path `/v1/…` hoặc `/v2/…`. Token gắn cả khi
 * đã đăng nhập: phiên đăng nhập có thể đã bị backend thu hồi (đổi mật khẩu, xoá
 * cache), khi đó backend coi request là khách và đòi token ẩn danh.
 *
 * Gặp 401 `error_description: "unauthorized"` (backend không nhận ra token — hết
 * hạn, Redis bị dọn) ⇒ bỏ token, xin lại, gửi lại ĐÚNG MỘT lần. Chỉ gửi lại khi
 * body đọc lại được (không phải stream).
 */
import { BOIDE_ACCOUNT_API_URL, BOIDE_API_URL } from "@/lib/boide-api-url";
import { clearAnonToken, getAnonToken } from "@/lib/anon-token";

const ANON_HEADER = "X-Anon-Token";
const INSTALLED = "__boideApiFetchGuard";

function apiOrigins(): Set<string> {
  const bases = [BOIDE_API_URL || window.location.origin, BOIDE_ACCOUNT_API_URL];
  return new Set(bases.filter(Boolean).map((base) => new URL(base, window.location.href).origin));
}

function isApiRequest(rawUrl: string): boolean {
  try {
    const url = new URL(rawUrl, window.location.href);
    if (!apiOrigins().has(url.origin)) return false;
    return /^\/v[12]\//.test(url.pathname) && url.pathname !== "/v1/anon/token";
  } catch {
    return false;
  }
}

function isReplayable(body: BodyInit | null | undefined): boolean {
  return !(typeof ReadableStream !== "undefined" && body instanceof ReadableStream);
}

async function isAnonUnauthorized(res: Response): Promise<boolean> {
  if (res.status !== 401) return false;
  try {
    const json = (await res.clone().json()) as { error_description?: unknown };
    return json?.error_description === "unauthorized";
  } catch {
    return false;
  }
}

export function installApiFetchGuard() {
  if (typeof window === "undefined") return;
  const w = window as unknown as Record<string, unknown>;
  if (w[INSTALLED]) return;
  w[INSTALLED] = true;

  const nativeFetch = window.fetch.bind(window);

  const send = (input: string | URL, init: RequestInit | undefined, token: string | null) => {
    const headers = new Headers(init?.headers);
    if (token) headers.set(ANON_HEADER, token);
    else headers.delete(ANON_HEADER);
    return nativeFetch(input, { ...init, headers });
  };

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    // Request object: app không dùng, để nguyên (headers/body của nó khó ghép an toàn).
    if (input instanceof Request) return nativeFetch(input, init);
    const rawUrl = typeof input === "string" ? input : input.href;
    if (!isApiRequest(rawUrl)) return nativeFetch(input, init);

    const token = await getAnonToken(nativeFetch);
    const res = await send(input, init, token);

    if (!isReplayable(init?.body) || !(await isAnonUnauthorized(res))) return res;

    clearAnonToken(token);
    const fresh = await getAnonToken(nativeFetch);
    if (!fresh || fresh === token) return res;
    return send(input, init, fresh);
  };
}
